import { Head, router } from '@inertiajs/react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  InputAdornment,
  Pagination as MuiPagination,
  TextField,
  Typography,
} from '@mui/material'
import AppsIcon from '@mui/icons-material/Apps'
import CodeIcon from '@mui/icons-material/Code'
import ForumIcon from '@mui/icons-material/Forum'
import MonitorIcon from '@mui/icons-material/Monitor'
import SearchIcon from '@mui/icons-material/Search'
import StorageIcon from '@mui/icons-material/Storage'
import WebIcon from '@mui/icons-material/Web'
import PermMediaIcon from '@mui/icons-material/PermMedia'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import React, { useState } from 'react'
import AppLayout from '../../components/layouts/AppLayout'
import type { StorefrontEntry, StorefrontCategory, Pagination } from '../../types'

const CATEGORY_ICONS: Record<string, React.ReactElement> = {
  Storage: <StorageIcon fontSize="small" />,
  Web: <WebIcon fontSize="small" />,
  Monitor: <MonitorIcon fontSize="small" />,
  Forum: <ForumIcon fontSize="small" />,
  Code: <CodeIcon fontSize="small" />,
  PermMedia: <PermMediaIcon fontSize="small" />,
  TaskAlt: <TaskAltIcon fontSize="small" />,
}

interface Props {
  entries: StorefrontEntry[]
  featuredEntries: StorefrontEntry[]
  categories: StorefrontCategory[]
  pagination: Pagination
  filters: { q?: string | null; category?: string | null }
}

function AppCard({ entry, showInstall }: { entry: StorefrontEntry; showInstall?: boolean }) {
  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: 4 },
      }}
    >
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
          {entry.logoUrl ? (
            <Box
              component="img"
              src={entry.logoUrl}
              alt={entry.name}
              sx={{ width: 48, height: 48, borderRadius: 1, objectFit: 'contain', mr: 2, flexShrink: 0 }}
            />
          ) : (
            <Box sx={{
              width: 48, height: 48, borderRadius: 1, mr: 2, flexShrink: 0,
              bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <AppsIcon sx={{ color: 'grey.500' }} />
            </Box>
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                {entry.name}
              </Typography>
              {showInstall && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={(e) => { e.stopPropagation(); router.visit(`/apps/${entry.vendorSlug}/${entry.slug}`) }}
                  sx={{ ml: 1, flexShrink: 0, borderRadius: 5 }}
                >
                  Install
                </Button>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" noWrap>
              {entry.vendorName}
            </Typography>
          </Box>
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            overflow: 'hidden', textOverflow: 'ellipsis',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            mb: 1.5, flexGrow: 1,
          }}
        >
          {entry.shortDescription || 'No description available'}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
          {entry.categories.slice(0, 3).map((cat) => (
            <Chip key={cat.slug} label={cat.name} size="small" variant="outlined" />
          ))}
          {entry.latestVersion && (
            <Chip label={`v${entry.latestVersion}`} size="small" color="primary" variant="outlined" />
          )}
        </Box>
      </CardContent>
    </Card>
  )
}

export default function Index({ entries, featuredEntries, categories, pagination, filters }: Props) {
  const [searchValue, setSearchValue] = useState(filters.q || '')

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    router.get('/apps', { q: searchValue || undefined, category: filters.category || undefined }, { preserveState: true })
  }

  const handleCategoryClick = (slug: string | null) => {
    router.get('/apps', { q: filters.q || undefined, category: slug || undefined }, { preserveState: true })
  }

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    router.get('/apps', { q: filters.q || undefined, category: filters.category || undefined, page }, { preserveState: true })
  }

  const handleCardClick = (entry: StorefrontEntry) => {
    router.visit(`/apps/${entry.vendorSlug}/${entry.slug}`)
  }

  return (
    <AppLayout noContainer>
      <Head title="Browse Apps - Okonomi App Store" />

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(180deg, #F5E6E0 0%, #FFF8F5 100%)',
          pt: 6,
          pb: 4,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h3" component="h1" align="center" sx={{ fontWeight: 700, mb: 1 }}>
            Browse Apps
          </Typography>
          <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Discover open-source apps you can deploy with Okonomi
          </Typography>

          <Box component="form" onSubmit={handleSearch} sx={{ maxWidth: 600, mx: 'auto', mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search for apps, databases, tools..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                },
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
            <Chip
              icon={<AppsIcon fontSize="small" />}
              label="All"
              variant={!filters.category ? 'filled' : 'outlined'}
              color={!filters.category ? 'primary' : 'default'}
              onClick={() => handleCategoryClick(null)}
            />
            {categories.map((cat) => (
              <Chip
                key={cat.id}
                icon={cat.icon ? CATEGORY_ICONS[cat.icon] : undefined}
                label={cat.name}
                variant={filters.category === cat.slug ? 'filled' : 'outlined'}
                color={filters.category === cat.slug ? 'primary' : 'default'}
                onClick={() => handleCategoryClick(cat.slug)}
              />
            ))}
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Featured Apps */}
        {featuredEntries.length > 0 && !filters.q && !filters.category && (
          <Box sx={{ mb: 5 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              Featured Apps
            </Typography>
            <Grid container spacing={3}>
              {featuredEntries.map((entry) => (
                <Grid key={entry.id} size={{ xs: 12, sm: 6 }}>
                  <Box
                    onClick={() => handleCardClick(entry)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <AppCard entry={entry} showInstall />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* All Apps */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          {filters.q || filters.category ? 'Results' : 'All Apps'}
        </Typography>

        {entries.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <AppsIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No apps found
            </Typography>
            <Typography color="text.secondary">
              Try adjusting your search or category filter.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {entries.map((entry) => (
              <Grid key={entry.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <Box
                  onClick={() => handleCardClick(entry)}
                  sx={{ cursor: 'pointer' }}
                >
                  <AppCard entry={entry} showInstall />
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        {pagination.totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <MuiPagination
              count={pagination.totalPages}
              page={pagination.page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}
      </Container>
    </AppLayout>
  )
}
